import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigationManager } from '@provider/navigation';
import { makeTanstackQuery } from '@provider/tanstack';
import { useUserIdentityState } from '@provider/user-identity';
import { DAYS_TO_MS, SECONDS_TO_MS } from '@utils/constants';
import { useGetRevisionFromURLQuery } from './query.get-from-url.js';
import { getAllRevisionsRequest } from './request.get-all.js';

const getAllRevisionsQuery = makeTanstackQuery({
  defaultQueryConfig: {
    staleTime: 15 * SECONDS_TO_MS,
    gcTime: 7 * DAYS_TO_MS,
    meta: {
      persist: true,
      revalidateOnRefresh: true
    }
  },
  queryKey: input => ["course-revisions/get-all", input.user?.id, input.courseId],
  queryFn: input => getAllRevisionsRequest(input.user, input.courseId),
  useQueryInput(queryFactory, externalInput) {
    const user = useUserIdentityState(state => state.user);
    return queryFactory({
      ...externalInput,
      user
    });
  }
});
const useGetAllRevisionsQuery = (courseId, extraConfig) => {
  //
  // STEP: load the user's own revisions into memory
  //
  const urlRevision = useGetRevisionFromURLQuery();
  const userRevisions = useQuery(getAllRevisionsQuery.useQueryConfig({
    courseId,
    queryConfig: extraConfig
  }));

  //
  // STEP: push the currently selected revisionId to the URL query string
  // parameter
  //
  const location = useLocation();
  const navigationManager = useNavigationManager();
  useEffect(() => {
    const revisionId = new URL(location.asPath, "http://localhost/").searchParams.get("revisionId");
    if (courseId != null && (urlRevision.data?.id === revisionId && urlRevision.data?.courseId !== courseId || urlRevision.data?.id === courseId)) {
      // remove revisionId from query if the fetched revision does not belong
      // to this specific course
      const url = new URL(navigationManager.getLocation().asPath, "http://localhost/");
      if (url.searchParams.get("revisionId") === revisionId) {
        url.searchParams.set("revisionId", "");
        url.searchParams.set("revisionTitle", "");
      }
      navigationManager.replace(url.pathname + url.search + url.hash);
    }
  }, [urlRevision.data, urlRevision.isPending, navigationManager, courseId, location.asPath]);
  return useMemo(() => {
    const revisionId = new URL(location.asPath, "http://localhost/").searchParams.get("revisionId");
    if (!userRevisions.data) {
      return userRevisions;
    }
    if (revisionId === courseId) {
      return {
        ...userRevisions,
        data: [...userRevisions.data.map(it => ({
          ...it,
          isActive: false
        }))]
      };
    }
    if (!urlRevision.data || urlRevision.data.courseId !== courseId) {
      return userRevisions;
    }
    const isUrlRevisionActive = urlRevision.data.id === revisionId;
    if (userRevisions.data.some(it => it.id === urlRevision.data?.id)) {
      return {
        ...userRevisions,
        data: [...userRevisions.data.map(it => ({
          ...it,
          isActive: isUrlRevisionActive ? urlRevision.data?.id === it.id : it.isActive
        }))]
      };
    }
    return {
      ...userRevisions,
      data: [{
        ...urlRevision.data,
        isActive: isUrlRevisionActive
      }, ...userRevisions.data.map(it => ({
        ...it,
        isActive: isUrlRevisionActive ? false : it.isActive
      }))]
    };
  }, [courseId, location.asPath, urlRevision.data, userRevisions]);
};

export { getAllRevisionsQuery, useGetAllRevisionsQuery };
//# sourceMappingURL=query.get-all.js.map
